'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cn, getInitials } from '@workspace/ui/lib';
import type { JwtPayload } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';
import { ChevronsUpDown, LoaderCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getOrganisationsByUserIds, updateCurrentOrganisation } from '@/actions/organisation';
import { useCanShowMultiOrganisationSwitcher } from '@/hooks/use-can-show-multi-organisation-switcher';
import { createJwtUserFromPayload, getActiveOrganisationFromUser } from '@/lib/active-organisation';
import {
  broadcastOrganisationSwitch,
  reloadAfterOrganisationSwitch,
} from '@/lib/organisation-switch-channel';
import { useAuthStore } from '@/stores/authStore';

interface MobileOrganisationSwitcherProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function MobileOrganisationSwitcher({ isOpen, setIsOpen }: MobileOrganisationSwitcherProps) {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const setAuthenticated = useAuthStore((state) => state.setAuthenticated);
  const setUser = useAuthStore((state) => state.setUser);
  const setIsLoading = useAuthStore((state) => state.setIsLoading);
  const setOrganisationSwitching = useAuthStore((state) => state.setOrganisationSwitching);

  const userId = user?.user.id;
  const activeOrganisationId = getActiveOrganisationFromUser(user)?.id;
  const canShowSwitcher = useCanShowMultiOrganisationSwitcher();

  const { data: organisationsResponse, isLoading } = useQuery({
    queryKey: ['organisations-by-user-ids', userId],
    queryFn: () => getOrganisationsByUserIds(userId ? [userId] : []),
    enabled: !!userId && canShowSwitcher,
    staleTime: 10 * 60 * 1000,
  });

  const organisations = organisationsResponse?.data ?? [];
  const activeOrganisation = organisations.find(
    (organisation) => organisation.id === activeOrganisationId
  );

  const switchMutation = useMutation({
    mutationFn: (organisationId: number) =>
      userId
        ? updateCurrentOrganisation({ user_id: userId, organisation_id: organisationId })
        : Promise.reject(new Error('Missing user id')),
    onMutate: () => {
      setOrganisationSwitching(true);
      setIsLoading(true);
    },
    onSuccess: (response, organisationId) => {
      const accessToken = response.data?.auth.access_token;
      if (!accessToken) {
        setOrganisationSwitching(false);
        setIsLoading(false);
        toast.error('Organisation switched, but no access token was returned.');
        return;
      }

      const decodedToken = jwtDecode<JwtPayload>(accessToken);
      setAccessToken(accessToken);
      setAuthenticated(true);
      setUser(createJwtUserFromPayload(decodedToken));
      queryClient.clear();
      broadcastOrganisationSwitch(organisationId);
      setIsOpen(false);
      reloadAfterOrganisationSwitch(organisationId);
    },
    onError: (error) => {
      setOrganisationSwitching(false);
      setIsLoading(false);
      toast.error(error instanceof Error ? error.message : 'Failed to switch organisation.');
    },
  });

  if (!canShowSwitcher || organisations.length <= 1) return null;

  return (
    <div className="flex flex-col">
      <button
        type="button"
        className="group hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-10 w-full cursor-pointer items-center gap-2 rounded-lg p-2 text-sm transition-colors"
        aria-label={'Organisation'}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="bg-neutral-200 text-neutral-100 flex size-6 shrink-0 items-center justify-center rounded-full text-[10px]">
          <p>
            {getInitials(
              activeOrganisation?.brand_name ?? activeOrganisation?.registered_name ?? ''
            )}
          </p>
        </div>
        <p className="text-sidebar-foreground flex-1 text-left">
          {activeOrganisation?.brand_name ?? activeOrganisation?.registered_name ?? 'Organisation'}
        </p>
        {switchMutation.isPending || isLoading ? (
          <LoaderCircle className="animate-spin" />
        ) : (
          <ChevronsUpDown />
        )}
      </button>

      {isOpen && (
        <div className="bg-grey-25 dark:bg-grey-1000 absolute top-0 left-0 z-10 flex min-h-full w-full flex-col gap-4">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center lg:gap-3">
            <h3 className="text-primary text-base font-medium dark:text-neutral-100">
              {'Organisations'}
            </h3>

            <hr className="border-grey-100 dark:border-grey-800 block w-full lg:hidden" />
          </div>

          <div className="flex flex-col gap-2">
            {organisations.map((organisation) => {
              const isActive = organisation.id === activeOrganisationId;
              return (
                <button
                  key={organisation.id}
                  type="button"
                  className={cn(
                    'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex min-h-12 w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                    isActive && 'bg-sidebar-accent'
                  )}
                  disabled={isActive || switchMutation.isPending}
                  onClick={() => switchMutation.mutate(organisation.id)}
                >
                  <div className="bg-neutral-200 text-neutral-100 flex size-7 shrink-0 items-center justify-center rounded-full text-xs">
                    <p>{getInitials(organisation.brand_name || organisation.registered_name)}</p>
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <p className="text-neutral-200 truncate font-medium dark:text-neutral-100">
                      {organisation.brand_name}
                    </p>
                    {organisation.registered_name && (
                      <p className="text-grey-600 truncate text-xs">
                        {organisation.registered_name}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
